import { useState, useCallback, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { ReferralStatus } from "@/lib/schema";
import { KanbanReferralCardItem } from "./KanbanReferralCardItem";
import { AdminReferralWithUI } from "@/api/referrals";
import { getReferralConfig, KanbanColumnConfig, createDynamicConfig } from "@/lib/kanbanConfigs.tsx";

// Interface is now imported from KanbanReferralCardItem

interface KanbanBoardProps {
  referrals: AdminReferralWithUI[];
  onStatusChange: (referralId: string, newStatus: ReferralStatus) => void;
  onViewReferral: (referral: AdminReferralWithUI) => void;
  configType?: 'candidate' | 'extended';
  customColumns?: KanbanColumnConfig[]; // Optional custom columns override
  syncingReferrals?: Set<string>; // IDs of referrals currently syncing
}

export function KanbanBoard({ referrals, onStatusChange, onViewReferral, configType = 'candidate', customColumns, syncingReferrals = new Set() }: KanbanBoardProps) {
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ReferralStatus | null>(null);
  
  // Get the status columns configuration - use custom columns if provided, otherwise use config
  const statusColumns = useMemo(() => customColumns || getReferralConfig(configType), [customColumns, configType]);

  const handleDragStart = useCallback((e: React.DragEvent, referralId: string) => {
    setDraggedItem(referralId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', referralId);
    
    // Create a custom drag image for better visual feedback
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.opacity = '0.8';
    e.dataTransfer.setDragImage(dragImage, 0, 0);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: ReferralStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only update drag over state if it's different
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  }, [dragOverColumn]);
  
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, newStatus: ReferralStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (draggedItem) {
      // Find the referral being moved
      const referral = referrals.find(r => r.id === draggedItem);
      
      // Only update if status is actually changing
      if (referral && referral.status !== newStatus) {
        // Non-blocking call - UI updates immediately, API call happens in background
        onStatusChange(draggedItem, newStatus);
      }
      
      // Clear drag state immediately - don't wait for API
      setDraggedItem(null);
    }
  }, [draggedItem, referrals, onStatusChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverColumn(null);
  }, []);

  // Memoize referrals by status to prevent unnecessary re-renders
  const referralsByStatus = useMemo(() => {
    const grouped: Record<string, AdminReferralWithUI[]> = {};
    referrals.forEach(referral => {
      if (!grouped[referral.status]) {
        grouped[referral.status] = [];
      }
      grouped[referral.status].push(referral);
    });
    return grouped;
  }, [referrals]);
  
  const getReferralsByStatus = useCallback((status: string) => {
    return referralsByStatus[status] || [];
  }, [referralsByStatus]);

  // Dynamic grid columns based on the number of status columns
  const getGridCols = (numColumns: number) => {
    switch (numColumns) {
      case 1: return 'lg:grid-cols-1';
      case 2: return 'lg:grid-cols-2';
      case 3: return 'lg:grid-cols-3';
      case 4: return 'lg:grid-cols-4';
      case 5: return 'lg:grid-cols-5';
      case 6: return 'lg:grid-cols-6';
      case 7: return 'lg:grid-cols-7';
      case 8: return 'lg:grid-cols-8';
      default: return 'lg:grid-cols-6'; // fallback
    }
  };

  return (
    <div className={`grid grid-cols-1 ${getGridCols(statusColumns.length)} gap-6 h-full`} style={{ scrollBehavior: 'smooth' }}>
      {statusColumns.map((column) => (
        <div
          key={column.id}
          className={`flex flex-col transition-all duration-200 ease-in-out ${
            draggedItem && dragOverColumn === column.id 
              ? 'bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg shadow-lg scale-[1.02]' 
              : draggedItem 
                ? 'bg-gray-50/50 border-2 border-transparent rounded-lg' 
                : 'border-2 border-transparent'
          }`}
          onDragOver={(e) => handleDragOver(e, column.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, column.id)}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-2">
              <div className={column.iconColor}>
                {column.icon}
              </div>
              <h3 className="font-semibold text-gray-900 space-grotesk">{column.title}</h3>
            </div>
            <Badge className={`${column.color} border-0 text-xs font-medium`}>
              {getReferralsByStatus(column.id).length}
            </Badge>
          </div>
          
          <div className={`flex-1 space-y-3 min-h-[400px] transition-all duration-200 ${
            draggedItem && dragOverColumn === column.id ? 'min-h-[420px]' : ''
          }`}>
            {getReferralsByStatus(column.id).map((referral) => (
              <div key={referral.id} className="relative">
                <KanbanReferralCardItem
                  referral={referral}
                  onViewReferral={onViewReferral}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  isDragging={draggedItem === referral.id}
                />
                {/* Syncing indicator */}
                {syncingReferrals.has(referral.id) && (
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full">
                      Syncing...
                    </span>
                  </div>
                )}
              </div>
            ))}
            
            {getReferralsByStatus(column.id).length === 0 && (
              <div className={`flex items-center justify-center h-32 border-2 border-dashed rounded-lg transition-all duration-200 ${
                draggedItem && dragOverColumn === column.id 
                  ? 'border-blue-400 bg-blue-50/50 text-blue-600' 
                  : 'border-gray-200 text-gray-500'
              }`}>
                <p className="text-sm space-grotesk font-medium">
                  {draggedItem && dragOverColumn === column.id 
                    ? `Drop to move to ${column.title}` 
                    : 'No referrals'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}